<script setup>
import { ref, computed, onMounted } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { ST } from '@/constants/ST'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idCondicaoPagamento: 0,
      descricao: '',
      quantidadeParcelas: 1,
      diasIntervalo: 30,
      diaFixoVencimento: null,
      entrada: false,
      lstStatus: ST.Ativo,
      idOrganizacao: null
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const mostrarAuditoria = ref(false)

const listas = ref({
  status: []
})

const isLoadingListas = ref(false)

const temPermissaoCompleta = computed(() => authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')))

const isReadOnly = computed(() => {
  return isEditing.value && !temPermissaoCompleta.value
})

const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const response = await api.get('/ListasSistema/lookup/ST')
    listas.value.status = response.data
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar lista de status.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

const salvar = async () => {
  if (isReadOnly.value) return

  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  // GUARDIÃO: Padrão de Arquitetura 1.6 (Spread Operator Limpo)
  const payload = {
    ...item.value
  }

  await submitForm('CondicoesPagamento', 'idCondicaoPagamento', payload)
}

const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

onMounted(() => {
  carregarListas()
})
</script>

<template>
  <FormularioCadastro
    module-name="Condição de Pagamento"
    :is-loading="isLoading || isLoadingListas"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="salvar"
    @cancelar="handleCancel"
    :desabilitar-salvar="isReadOnly"
  >
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="abrirAuditoria"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">Dados Gerais e Regras</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <!-- GUARDIÃO: Os "ids" dos componentes mapeiam EXATAMENTE o DTO para a Validação Híbrida do DOM funcionar -->
          <div class="col-md-8">
            <CampoInput
              id="descricao"
              label="Descrição / Referência"
              v-model="item.descricao"
              placeholder="Ex: 30/60/90 Dias com Entrada"
              maxlength="100"
              :readonly="isReadOnly"
              required
            />
          </div>

          <div class="col-md-4">
            <CampoSelect
              id="lstStatus"
              label="Status"
              v-model="item.lstStatus"
              :options="listas.status"
              option-value="idListaSistemaItem"
              option-label="textoLista"
              :disabled="isReadOnly || isLoadingListas"
              required
            />
          </div>

          <div class="col-md-3">
            <CampoSelect
              id="entrada"
              label="Possui Entrada (D+0)?"
              v-model="item.entrada"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              :disabled="isReadOnly"
              required
              descricao-informativa="A 1ª parcela vence no ato do faturamento."
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="quantidadeParcelas"
              label="Qtd. de Parcelas"
              type="number"
              min="1"
              v-model="item.quantidadeParcelas"
              :readonly="isReadOnly"
              required
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="diasIntervalo"
              label="Intervalo (Dias)"
              type="number"
              min="0"
              v-model="item.diasIntervalo"
              :readonly="isReadOnly"
              required
              descricao-informativa="Dias entre cada vencimento."
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="diaFixoVencimento"
              label="Dia Fixo Vencimento"
              type="number"
              min="1"
              max="31"
              v-model="item.diaFixoVencimento"
              :readonly="isReadOnly"
              placeholder="Opcional"
              descricao-informativa="Força o vencimento para este dia."
            />
          </div>

        </div>
      </div>
    </div>

  </FormularioCadastro>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="CondicaoPagamento"
    :chave-primaria="item.idCondicaoPagamento"
    :titulo-registro="item.descricao"
    contexto="tenant"
    :dicionarios="{ 'lstStatus': listas.status }"
    @fechar="mostrarAuditoria = false"
  />
</template>
